import ArcadeConfig, { ATTRIBUTES as ARCADE_CONFIG_ATTRIBUTES } from './data/arcadeConfig';
import './lib/modernizr-custom';
import { getLogger } from './logging/logger';

const logger = getLogger('Environment');

export default class Environment {
	static getSearchParam(param) {
		return new URLSearchParams(window.location.search).get(param);
	}

	static isLocal() {
		return /:808[01]/.test(window.location.origin);
	}

	static isDev() {
		return /dev/i.test(window.location.origin);
	}

	static isDebug() {
		return /true/i.test(this.getSearchParam('debug'));
	}

	static isProd() {
		return !this.isDev() && !this.isLocal();
	}

	static isAndroidTv() {
		return /Chromecast|Stream TV|Android TV|BRAVIA/.test(navigator.userAgent);
	}

	static isAndroidMobile() {
		return /Android/.test(navigator.userAgent) && !this.isAndroidTv();
	}

	// Intended to specifically be used for cordova functionality
	static isCordova() {
		return this.isAndroidTv() || this.isAndroidMobile();
	}

	static isTizen() {
		return /Tizen/.test(navigator.userAgent);
	}

	static isWebOS() {
		return /webOS|Web0S/.test(navigator.userAgent);
	}

	/**
	 * Checks if STB on any supported TV based platforms
	 */
	static isSetTopBox() {
		return (
			this.isComcastSetTopBox() ||
			this.isVerizonSetTopBox() ||
			this.isAndroidTv() ||
			this.isTizen() ||
			this.isWebOS() ||
			/STB/.test(this.getSearchParam('utm_medium')) // Added so testers will see more realistic functionality of arrow input
		);
	}

	/**
	 * Checks if Comcast/Cox STB
	 */
	static isComcastSetTopBox() {
		return /(NativeXREReceiver|WPE)/.test(navigator.userAgent);
	}

	/**
	 * Checks if Verizon owned STB
	 * ! The check for 'Stream TV' may need to be removed in the future. I was placed here with the rumor that the same verizon integration can be used on android devices
	 */
	static isVerizonSetTopBox() {
		return /(Verizon|KreaTV|Stream TV)/.test(navigator.userAgent);
	}

	static isVerizon1100() {
		return /(IPC|VMS)1100/i.test(navigator.userAgent);
	}

	static isVerizon4100() {
		return /(IPC|VMS)4100/i.test(navigator.userAgent);
	}

	static isLowMemory() {
		return this.isVerizon1100();
	}

	static isMTheory() {
		return /M-Theory-Chromium/.test(navigator.userAgent);
	}

	static needsVideoControls() {
		return this.isLocal();
	}

	static isKidsVariant() {
		return /hwpHubKids/.test(this.getSearchParam('utm_campaign'));
	}

	static isHub() {
		const arcadeName = ArcadeConfig.get(ARCADE_CONFIG_ATTRIBUTES.arcadeName);
		return (
			/\/hwpHub(?!Games).*\//i.test(window.location.pathname) ||
			/hwpHub(?!Games).*$/i.test(arcadeName)
		);
	}

	static isInHub() {
		logger.log(`[${this.isInHub.name}]`, { referer: document.referrer });

		if (!document.referrer) {
			return false;
		}

		if (this.isLocal()) {
			// Determine if the referrer was running on a different port. Assume this means it's the hub and we are child.
			const referrer = new URL(document.referrer);
			const referrerPort = referrer.port;
			const locationPort = window.location.port;

			const isInHub =
				Boolean(referrerPort) && Boolean(locationPort) && referrerPort !== locationPort;
			logger.debug('isLocal', { isInHub, referrerPort, locationPort });
			return isInHub;
		}

		const gameType = this.getSearchParam('gameType');
		const contentUrl = this.getSearchParam('contentUrl');
		logger.debug({ gameType, contentUrl });
		if (gameType || contentUrl) {
			// gameType || contentUrl indicates we are in a hub arcade
			logger.debug('Detected content query params for in hub experience.');
			return true;
		}

		// second check: iff in hub, loaded a module into self, always loads index and !test|demo
		const isHwpDomain = /hollywoodplayer.(com|tv)\/hwpHub(?!Games).*\//.test(document.referrer);
		const isNotTestOrDemo = !/(test|demo).html/.test(document.referrer);
		logger.debug('Checking if in hub based on referrer.', { isHwpDomain, isNotTestOrDemo });
		return isHwpDomain && isNotTestOrDemo;
	}

	static getPlatform() {
		if (this.isComcastSetTopBox()) {
			return 'cox';
		}

		if (this.isVerizonSetTopBox()) {
			return 'verizon';
		}

		return this.getSearchParam('utm_source') || 'web';
	}
}
